import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import { query } from "../api/client";
import type { LeadFacets, LeadPage } from "../api/types";
import { useResource } from "../hooks/useResource";
import { EmptyState, ErrorBanner, LoadingState, PageHeader, QualityChip } from "../components/common";

/**
 * The triage queue.
 *
 * Filters live in the URL, so an operator can bookmark "fresh Boston leads that
 * passed the gate" and hand that link to someone else — the thing a Slack feed
 * fundamentally cannot do.
 */

const DELIVERY_OPTIONS = [
  { value: "", label: "Any delivery" },
  { value: "ok", label: "Delivered" },
  { value: "error", label: "Delivery failed" },
  { value: "none", label: "Not delivered" },
];

const ASSIGNED_OPTIONS = [
  { value: "", label: "Any assignment" },
  { value: "true", label: "Assigned" },
  { value: "false", label: "Held / unassigned" },
];

export function LeadsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get("q") ?? "");

  const get = (key: string, fallback = "") => params.get(key) ?? fallback;
  const limit = Number(get("limit", "50")) || 50;
  const offset = Number(get("offset", "0")) || 0;

  const set = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    // Any filter change invalidates the page cursor.
    if (!("offset" in changes)) next.delete("offset");
    setParams(next, { replace: true });
  };

  const path = useMemo(
    () =>
      `/api/leads${query({
        market: get("market"),
        quality: get("quality"),
        signal: get("signal"),
        state: get("state"),
        assigned: get("assigned"),
        deliveryStatus: get("deliveryStatus"),
        maxDaysOld: get("maxDaysOld"),
        q: get("q"),
        sort: get("sort", "event_at"),
        dir: get("dir", "desc"),
        limit,
        offset,
      })}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params.toString()],
  );

  const feed = useResource<LeadPage>(path);
  const facets = useResource<LeadFacets>("/api/leads/facets");

  const sortKey = get("sort", "event_at");
  const direction = get("dir", "desc") === "asc" ? "asc" : "desc";
  const toggleSort = (key: string) => {
    if (key === sortKey) set({ dir: direction === "asc" ? "desc" : "asc" });
    else set({ sort: key, dir: "desc" });
  };

  return (
    <Box>
      <PageHeader
        title="Leads"
        subtitle="Every assembled lead with the reason it is worth a call. Filters are in the URL — share the link."
        actions={
          <Button startIcon={<RefreshIcon />} onClick={feed.reload}>
            Refresh
          </Button>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 1.5 }}>
          <TextField
            label="Search address, payee or reason"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") set({ q: search.trim() });
            }}
            onBlur={() => set({ q: search.trim() })}
            sx={{ minWidth: 280, flexGrow: 1 }}
          />
          <TextField
            select
            label="Market"
            value={get("market")}
            onChange={(e) => set({ market: e.target.value })}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All markets</MenuItem>
            {(facets.data?.markets ?? []).map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Quality"
            value={get("quality")}
            onChange={(e) => set({ quality: e.target.value })}
            sx={{ minWidth: 150 }}
            helperText="ok = shippable"
          >
            <MenuItem value="">All qualities</MenuItem>
            {(facets.data?.qualities ?? []).map((q) => (
              <MenuItem key={q} value={q}>
                {q}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Signal"
            value={get("signal")}
            onChange={(e) => set({ signal: e.target.value })}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All signals</MenuItem>
            {(facets.data?.signals ?? []).map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Assignment"
            value={get("assigned")}
            onChange={(e) => set({ assigned: e.target.value })}
            sx={{ minWidth: 170 }}
          >
            {ASSIGNED_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Delivery"
            value={get("deliveryStatus")}
            onChange={(e) => set({ deliveryStatus: e.target.value })}
            sx={{ minWidth: 170 }}
          >
            {DELIVERY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Max age (days)"
            type="number"
            value={get("maxDaysOld")}
            onChange={(e) => set({ maxDaysOld: e.target.value })}
            sx={{ width: 140 }}
            helperText="Excludes undated leads"
            inputProps={{ min: 0 }}
          />
        </Stack>
      </Paper>

      <ErrorBanner error={feed.error} onRetry={feed.reload} />

      {feed.loading && !feed.data ? <LoadingState label="Loading leads…" /> : null}

      {feed.data && feed.data.items.length === 0 ? (
        <EmptyState
          title="No leads match these filters"
          hint="Clear a filter, or run the pipeline from the dashboard if nothing has been assembled yet."
        />
      ) : null}

      {feed.data && feed.data.items.length > 0 ? (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "quality"}
                      direction={direction}
                      onClick={() => toggleSort("quality")}
                    >
                      Quality
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Property</TableCell>
                  <TableCell>Why now</TableCell>
                  <TableCell>Payee</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "res_units"}
                      direction={direction}
                      onClick={() => toggleSort("res_units")}
                    >
                      Units
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "event_at"}
                      direction={direction}
                      onClick={() => toggleSort("event_at")}
                    >
                      Event
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "market"}
                      direction={direction}
                      onClick={() => toggleSort("market")}
                    >
                      Market
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Assigned</TableCell>
                  <TableCell>Delivery</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feed.data.items.map((row) => (
                  <TableRow
                    key={row.lead_id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/leads/${row.lead_id}`)}
                  >
                    <TableCell>
                      <QualityChip quality={row.quality} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Typography variant="body2" noWrap title={row.address ?? ""}>
                        {row.address ?? "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[row.city ?? row.borough, row.state, row.zip].filter(Boolean).join(" · ") || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
                        {row.why_now ?? row.signal_label ?? row.signal_key}
                      </Typography>
                      {row.signal_kind === "stock" ? (
                        <Tooltip title="A standing condition, not dated news. There is no deadline behind it.">
                          <Chip label="stock" variant="outlined" sx={{ mt: 0.5 }} />
                        </Tooltip>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" noWrap title={row.org ?? ""}>
                        {row.org ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{row.res_units ?? "—"}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.event_at ? row.event_at.slice(0, 10) : "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.days_old === null ? "undated" : `${row.days_old}d old`}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.market ?? "—"}</TableCell>
                    <TableCell>{row.assigned_client ?? "—"}</TableCell>
                    <TableCell>
                      {row.delivery_status ? (
                        <Chip
                          label={row.delivery_status}
                          color={row.delivery_status === "ok" ? "success" : "error"}
                          variant="outlined"
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={feed.data.total}
            page={Math.floor(offset / limit)}
            rowsPerPage={limit}
            rowsPerPageOptions={[25, 50, 100, 200]}
            onPageChange={(_e, page) => set({ offset: String(page * limit) })}
            onRowsPerPageChange={(e) => set({ limit: e.target.value, offset: "0" })}
          />
        </Paper>
      ) : null}
    </Box>
  );
}
